// helper for acs lists
import { DefaultApi, JsGetActiveContractsResponse } from '@digitalasset/json-api-ts-client';
import { LRUCache } from 'typescript-lru-cache';
import { LRUCacheOptions } from 'typescript-lru-cache/src/LRUCache';
import { ACSContainer, ACSKey } from './ACSContainer';
import { WSSupport } from './WSSupport';

const USE_LOCAL_STORAGE = true;
const DEBUG_LOCAL_STORAGE = false;

export class ACSHelper {
  private readonly CACHE_OPTIONS: LRUCacheOptions<string, ACSContainer> = {
    maxSize: 50,
    entryExpirationTimeInMS: 10 * 60 * 1000, // 10 minutes
    onEntryEvicted: (entry) => {
      if (!USE_LOCAL_STORAGE) {
        return;
      }
      if (DEBUG_LOCAL_STORAGE) {
        console.log(`Storing ACSContainer (cache) for key  ${entry.key}`);
      }
      try {
        localStorage.setItem(entry.key, JSON.stringify(entry.value));
      } catch (e) {
        if (e instanceof DOMException) {
          console.error(
            `Failed to store ACSContainer (cache) for key  ${entry.key} in localStorage: ${e.message}`,
          ); // possibly quota exceeded
        } else {
          throw e;
        }
      }
    },
  };

  private contractsSet: LRUCache<string, ACSContainer> = new LRUCache(this.CACHE_OPTIONS);

  constructor(
    private readonly apiInstance: DefaultApi,
    private readonly wsSupport: WSSupport,
  ) {}

  private static createKey(party?: string, templateId?: string, interfaceId?: string): ACSKey {
    return { party, templateId, interfaceId };
  }

  private static keyToString(key: ACSKey): string {
    return `${key.party ? key.party : 'ANY'}_T:${key.templateId ?? '()'}_I:${key.interfaceId ?? '()'}`;
  }

  private findACSContainer(key: ACSKey): ACSContainer {
    const keyStr = ACSHelper.keyToString(key);
    const existing = this.contractsSet.get(keyStr);

    if (existing) {
      return existing;
    }
    if (USE_LOCAL_STORAGE) {
      const persistedData = localStorage.getItem(keyStr);
      if (persistedData) {
        try {
          const parsed = JSON.parse(persistedData) as ACSContainer;
          const restored = new ACSContainer(parsed);
          this.contractsSet.set(keyStr, restored);
          if (DEBUG_LOCAL_STORAGE) {
            console.log(`Restored ACSContainer (cache) for key  ${keyStr} from localStorage`);
          }
          return restored;
        } catch (e) {
          if (e instanceof DOMException) {
            console.error(
              `Failed to restore ACSContainer (cache) for key  ${keyStr} from localStorage: ${e.message}`,
            );
          } else {
            throw e;
          }
        }
      }
    }

    const newContainer = new ACSContainer();
    this.contractsSet.set(keyStr, newContainer);
    return newContainer;
  }

  async activeContractsForTemplate(
    offset: number,
    partyFilter: string,
    templateId: string,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    const key = ACSHelper.createKey(partyFilter, templateId, undefined);
    const container = this.findACSContainer(key);
    return container.update(offset, key, this.apiInstance, this.wsSupport);
  }

  async activeContractsForInterface(
    offset: number,
    partyFilter: string | undefined,
    interfaceId: string,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    const key = ACSHelper.createKey(partyFilter, undefined, interfaceId);
    const container = this.findACSContainer(key);
    return container.update(offset, key, this.apiInstance, this.wsSupport);
  }
}
